import {
  AbstractMesh,
  AssetContainer,
  PBRMaterial,
  Scene,
  SceneLoader,
  TransformNode,
  Vector3,
} from "@babylonjs/core";
import "@babylonjs/loaders/glTF";
import { MAX_LIGHTS_PER_MATERIAL } from "./lighting.js";

/**
 * Bundled CC0 photoscanned props (polyhaven.com), replacing the primitive
 * "programmer art" fixtures (sphere-blob trees, box furniture) that read as
 * a toy world. Each model loads ONCE per scene into an AssetContainer;
 * placements are instanced copies (instantiateModelsToScene with
 * doNotInstantiate: false), so 40 corridor plants share geometry and
 * materials instead of costing 40 separate uploads and draw states.
 *
 * Zones call instantiate() synchronously during build, so preload() must
 * complete before world construction starts (main.ts awaits it alongside the
 * shop fetch). Every load failure is caught: instantiate() returns null and
 * the caller keeps its procedural fallback, so a bad download can never
 * break world build.
 */

export type PropName = "potted_plant_02" | "plastic_monobloc_chair_01";

export const PROP_NAMES: PropName[] = [
  "potted_plant_02",
  "plastic_monobloc_chair_01",
];

/** Unscaled model heights in metres, read from the glTF accessor bounds of
 *  the bundled files (client/public/models/props). Used for target-height
 *  scaling without forcing a world-matrix computation at load time. */
const NATURAL_HEIGHT: Record<PropName, number> = {
  potted_plant_02: 0.63,
  plastic_monobloc_chair_01: 0.88,
};

export interface InstantiateOptions {
  rotationY?: number;
  /** Uniform scale factor. Ignored when targetHeight is set. */
  scale?: number;
  /** Scale so the prop stands this many metres tall. */
  targetHeight?: number;
}

const CACHE: WeakMap<Scene, PropLibrary> = new WeakMap();

export class PropLibrary {
  private readonly scene: Scene;
  private readonly containers: Map<PropName, AssetContainer> = new Map();
  private counter: number = 0;

  private constructor(scene: Scene) {
    this.scene = scene;
  }

  static for(scene: Scene): PropLibrary {
    const cached: PropLibrary | undefined = CACHE.get(scene);
    if (cached !== undefined) {
      return cached;
    }
    const lib: PropLibrary = new PropLibrary(scene);
    CACHE.set(scene, lib);
    return lib;
  }

  /** Load every bundled prop in parallel. Safe to call more than once. */
  async preload(): Promise<void> {
    await Promise.all(PROP_NAMES.map((name: PropName) => this.load(name)));
  }

  isAvailable(name: PropName): boolean {
    return this.containers.has(name);
  }

  private async load(name: PropName): Promise<void> {
    if (this.containers.has(name)) {
      return;
    }
    try {
      const container: AssetContainer = await SceneLoader.LoadAssetContainerAsync(
        `/models/props/${name}/`,
        `${name}.gltf`,
        this.scene
      );
      for (const mat of container.materials) {
        if (mat instanceof PBRMaterial) {
          // Same falloff convention as materials.ts: the mall's PointLight
          // intensities are tuned for legacy falloff. The light cap keeps
          // shader uniform-block counts inside mobile GPU limits (see
          // lighting.ts MAX_LIGHTS_PER_MATERIAL).
          mat.usePhysicalLightFalloff = false;
          mat.maxSimultaneousLights = MAX_LIGHTS_PER_MATERIAL;
        }
      }
      // Instances inherit receiveShadows from their source; setting it on
      // the sources here covers every future placement in one line.
      for (const mesh of container.meshes) {
        mesh.receiveShadows = true;
      }
      this.containers.set(name, container);
    } catch (err: unknown) {
      console.error(`[PropLibrary] failed to load ${name}:`, err);
    }
  }

  /**
   * Place one instanced copy under `parent` at `position` (parent-local
   * space). Returns the wrapper node, or null when the model isn't loaded
   * (callers keep their procedural fallback).
   */
  instantiate(
    name: PropName,
    parent: TransformNode,
    position: Vector3,
    options: InstantiateOptions = {}
  ): TransformNode | null {
    const container: AssetContainer | undefined = this.containers.get(name);
    if (container === undefined) {
      return null;
    }
    this.counter += 1;
    const id: number = this.counter;
    const node: TransformNode = new TransformNode(`prop-${name}-${id}`, this.scene);
    node.parent = parent;
    node.position = position.clone();
    if (options.rotationY !== undefined) {
      node.rotation.y = options.rotationY;
    }
    let scale: number = options.scale === undefined ? 1 : options.scale;
    if (options.targetHeight !== undefined) {
      scale = options.targetHeight / NATURAL_HEIGHT[name];
    }
    node.scaling.setAll(scale);

    const entries = container.instantiateModelsToScene(
      (sourceName: string) => `prop-${name}-${id}-${sourceName}`,
      false,
      { doNotInstantiate: false }
    );
    for (const root of entries.rootNodes) {
      root.parent = node;
    }
    for (const mesh of node.getChildMeshes()) {
      mesh.isPickable = false;
    }
    return node;
  }
}

/** All meshes of an instantiated prop (for shadow-caster registration). */
export function propMeshes(node: TransformNode): AbstractMesh[] {
  return node.getChildMeshes();
}
